import PropTypes from "prop-types";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import {
  faSync,
  faExpand,
  faCompress,
  faCheckSquare,
  faSquare,
} from "@fortawesome/free-solid-svg-icons";
import RequestsTable from "./RequestsTable";
import ActionButton from "../ui/ActionButton";
import ColumnVisibilityMenu from "../DonateMaterials/ColumnVisibilityMenu";
import { useColumnVisibility } from "../../hooks/useColumnVisibility";
import { useTableResize } from "../../hooks/useTableResize";
import { useRowSelection } from "../../hooks/useRowSelection";
import "../../style/RequestsAccordion.css";

// default column visibility for requests table
const DEFAULT_REQUESTS_VISIBILITY = {
  id: true,
  status: true,
  material: true,
  receiver_company: true,
  quantity_range: true,
  allocated_qty: true,
  scheduled_date: true,
  time: false,
  allocated_cost: true,
  date_modified: true,
  listing_id: false,
};

const RequestsAccordion = ({
  expanded,
  onAccordionChange,
  requestsTableRef,
  requestsData,
  darkMode,
  handleRefresh,
  isRefreshing,
  onAllocate,
  onRequestChange,
  onDecline,
}) => {
  const { t } = useTranslation();

  // use custom hooks for state management
  const {
    columnVisibility,
    setColumnVisibility,
    columnMenuOpen,
    setColumnMenuOpen,
  } = useColumnVisibility(
    "requestsTable_columnVisibility",
    DEFAULT_REQUESTS_VISIBILITY
  );

  const {
    tableHeight,
    isDragging,
    containerRef,
    maxTableHeight,
    handleMouseDown,
    toggleTableExpansion,
  } = useTableResize(600, requestsData.length);

  const {
    selectedRowIds,
    setSelectedRowIds,
    handleSelectAll,
    handleUnselectAll,
  } = useRowSelection();

  return (
    <div className="requests-accordion-container">
      <Accordion
        expanded={expanded === "requests-accordion"}
        onChange={onAccordionChange("requests-accordion")}
        className="requests-accordion"
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className="accordion-summary"
        >
          {/* Toolbar buttons in header - only show when expanded */}
          {expanded === "requests-accordion" && (
            <div
              className="accordion-header-toolbar"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Left side - Selection info */}
              {selectedRowIds.length > 0 && (
                <span className="selection-badge">
                  {selectedRowIds.length} {t("selected")}
                </span>
              )}

              {/* Spacer pushes buttons to right */}
              <div className="toolbar-spacer" />

              {/* Right side - Action buttons */}
              <ActionButton
                variant="default"
                icon={faSync}
                iconOnly
                tooltip={t("Refresh")}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefresh();
                }}
                disabled={isRefreshing}
                spinning={isRefreshing}
              />

              <ColumnVisibilityMenu
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={setColumnVisibility}
                onOpenChange={setColumnMenuOpen}
                darkMode={darkMode}
                variant={columnMenuOpen ? "success" : "default"}
                isRequestsTable={true}
              />

              <ActionButton
                variant={
                  tableHeight >= maxTableHeight - 20 ? "success" : "default"
                }
                icon={
                  tableHeight >= maxTableHeight - 20 ? faCompress : faExpand
                }
                iconOnly
                tooltip={
                  tableHeight >= maxTableHeight - 20
                    ? t("Collapse")
                    : t("Expand All")
                }
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTableExpansion();
                }}
              />

              {selectedRowIds.length === requestsData.length &&
              requestsData.length > 0 ? (
                <ActionButton
                  variant="secondary"
                  icon={faSquare}
                  iconOnly
                  tooltip={t("Unselect All")}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUnselectAll();
                  }}
                />
              ) : (
                <ActionButton
                  variant="default"
                  icon={faCheckSquare}
                  iconOnly
                  tooltip={t("Select All")}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll(requestsData);
                  }}
                />
              )}
            </div>
          )}
        </AccordionSummary>

        <AccordionDetails className="accordion-details">
          {/* Table Section */}
          <div className="table-section">
            <div ref={containerRef} className="table-container-wrapper">
              <div
                className={`table-container ${
                  isRefreshing ? "refreshing" : ""
                }`}
                style={{
                  height: `${tableHeight}px`,
                  maxHeight: `${tableHeight}px`,
                  overflow:
                    tableHeight >= maxTableHeight - 20 ? "hidden" : "auto",
                }}
              >
                <RequestsTable
                  ref={requestsTableRef}
                  data={requestsData}
                  darkMode={darkMode}
                  height="100%"
                  onSelectionChange={setSelectedRowIds}
                  externalSelection={selectedRowIds}
                  columnVisibility={columnVisibility}
                  onAllocate={onAllocate}
                  onRequestChange={onRequestChange}
                  onDecline={onDecline}
                />
              </div>

              {/* Resize Handle */}
              <div
                className={`resize-handle ${isDragging ? "dragging" : ""}`}
                onMouseDown={handleMouseDown}
              >
                <div className="resize-handle-bar" />
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

RequestsAccordion.propTypes = {
  expanded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  onAccordionChange: PropTypes.func.isRequired,
  requestsTableRef: PropTypes.object.isRequired,
  requestsData: PropTypes.array.isRequired,
  darkMode: PropTypes.bool.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  isRefreshing: PropTypes.bool,
  onAllocate: PropTypes.func.isRequired,
  onRequestChange: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
};

export default RequestsAccordion;
